import { useRef } from "react";
import type {
    DataGridListEditBase,
    DataGridListEditorProps,
} from "../../../columns/listEditors";
import { InlineEditor } from "../../editors/InlineEditor";
import { ListEditTrigger } from "../ListEditTrigger";
import { toCloseAndCommit } from "../listEditActions";

interface TextListEditorProps<TData> extends DataGridListEditorProps<TData> {
    edit: DataGridListEditBase<TData, string>;
}

type TextListInputProps<TData> = Pick<
    TextListEditorProps<TData>,
    "row" | "columnLabel" | "onOpenChange" | "onCloseAndCommit" | "edit"
>;

/**
 * The open editor: Enter, Tab, and blur close it, then commit the trimmed
 * text; blank text and Escape close it unchanged. Settles once, so the blur
 * from closing doesn't commit again.
 */
const TextListInput = <TData,>({
    row,
    columnLabel,
    onOpenChange,
    onCloseAndCommit,
    edit,
}: Readonly<TextListInputProps<TData>>) => {
    const hasSettledRef = useRef(false);
    const commit = toCloseAndCommit({ edit, row, onCloseAndCommit });
    const settle = (value: string | null) => {
        if (hasSettledRef.current) {
            return;
        }
        hasSettledRef.current = true;
        if (value === null) {
            onOpenChange(false);
            return;
        }
        commit(value);
    };
    return (
        <InlineEditor
            initialText={edit.value(row)}
            isReplacing={false}
            ariaLabel={columnLabel}
            align="start"
            placeholder={edit.placeholder}
            onCommit={({ text }) => {
                const value = text.trim();
                settle(value === "" ? null : value);
                return undefined;
            }}
            onCancel={() => settle(null)}
        />
    );
};

/**
 * `col.edit.text`: the text in a trigger that wraps only the text, so the rest
 * of the cell still opens the row; open, the grid's inline input in its place.
 */
export const TextListEditor = <TData,>({
    row,
    trigger,
    isOpen,
    onOpenChange,
    onCloseAndCommit,
    columnLabel,
    edit,
}: Readonly<TextListEditorProps<TData>>) =>
    isOpen ? (
        <TextListInput
            row={row}
            columnLabel={columnLabel}
            onOpenChange={onOpenChange}
            onCloseAndCommit={onCloseAndCommit}
            edit={edit}
        />
    ) : (
        <ListEditTrigger {...trigger} onClick={() => onOpenChange(true)} />
    );
